import { Post } from 'components/modules/card/jobPost/jobPost.type';
import JobPostCard from 'components/modules/card/jobPost/JobPostCard';
import React, { useState } from 'react';
import { Col, FormLabel, Row } from 'react-bootstrap';
import { Modal, Props } from './jobPostList.type';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Type } from '../add/addJobPost.type';
import ConfirmationDialog from 'components/base/ConfirmationDialog';
import DatePicker from 'components/base/DatePicker';
import {
  useUpdateJobAsCloseMutation,
  useUpdateJobAsOpenMutation
} from 'service/recruiterQuery/recruiterQuery';

const pattern = (item: Post) => {
  const {
    role: { value },
    deadline,
    companyName
  } = item;
  return `${companyName} is hiring a ${value}. Apply before ${dayjs(
    deadline
  ).format('MMMM D, YYYY')}, to drive innovation!`;
};

const navigatePathMapping: Record<Type, string> = {
  internship: '/panel/recruitment/careers/internships/edit/',
  post: '/panel/recruitment/careers/jobs/edit/'
};

const navigateViewPathMapping: Record<Type, string> = {
  internship: '/panel/recruitment/careers/internships/view/',
  post: '/panel/recruitment/careers/jobs/view/'
};

const initialModal = {
  type: undefined,
  id: '',
  date: ''
};

function JobPostList({ data, type, refetch }: Props) {
  const [modal, setModal] = useState<Modal>(initialModal);
  const [updateJobAsClose, { isLoading: updateJobAsCloseLoader }] =
    useUpdateJobAsCloseMutation();
  const [updateJobAsOpen, { isLoading: updateJobAsOpenLoader }] =
    useUpdateJobAsOpenMutation();

  const navigate = useNavigate();
  const handleEdit = (value: Post) => {
    navigate(navigatePathMapping[type] + value._id);
  };

  const handleView = (value: Post) => {
    navigate(navigateViewPathMapping[type] + value._id);
  };
  const handleDateChange = (value: Date[]) => {
    setModal(prev => ({ ...prev, date: value[0] }));
  };
  const handleMarkClose = () => {
    updateJobAsClose({ id: modal.id })
      .unwrap()
      .then(() => {
        setModal(initialModal);
        refetch();
      })
      .catch(console.log);
  };
  const handleMarkOpen = () => {
    updateJobAsOpen({ id: modal.id, deadline: modal.date })
      .unwrap()
      .then(() => {
        setModal(initialModal);
        refetch();
      })
      .catch(console.log);
  };
  return (
    <>
      <ConfirmationDialog
        show={modal.type === 'markClosed'}
        onConfirm={handleMarkClose}
        onHide={() => setModal(initialModal)}
        title="Confirm Close Action"
        isLoading={updateJobAsCloseLoader}
        confirmButtonLabel="Mark as closed"
      >
        <h5>Are you sure you want to mark this as closed?</h5>
        <p className="text-body-tertiary text-opacity-85">
          This action is irreversible and cannot be undone.
        </p>
      </ConfirmationDialog>
      <ConfirmationDialog
        show={modal.type === 'markActive'}
        onConfirm={handleMarkOpen}
        onHide={() => setModal(initialModal)}
        title="Confirm Active Action"
        isLoading={updateJobAsOpenLoader}
        confirmButtonLabel="Active"
        disabled={!modal.date}
      >
        <h5>Are you sure you want to activate this post?</h5>
        <div className="mb-2">
          <FormLabel>Deadline</FormLabel>
          <DatePicker
            placeholder="Select"
            onChange={handleDateChange}
            value={modal.date}
            options={{
              minDate: 'today'
            }}
          />
        </div>
        <p className="text-body-tertiary text-opacity-85 text-xs">
          To activate this post, please select a deadline. This action will make
          the post visible and enforce the chosen deadline for expiration.
        </p>
      </ConfirmationDialog>
      <ConfirmationDialog
        show={modal.type === 'reopen'}
        onConfirm={handleMarkOpen}
        onHide={() => setModal(initialModal)}
        title="Confirm Active Action"
        isLoading={updateJobAsOpenLoader}
        confirmButtonLabel="Active"
        disabled={!modal.date}
      >
        <h5>Are you sure you want to reopen this post?</h5>
        <div className="mb-2">
          <FormLabel>Deadline</FormLabel>
          <DatePicker
            placeholder="Select"
            onChange={handleDateChange}
            value={modal.date}
          />
        </div>
        <p className="text-body-tertiary text-opacity-85 text-xs">
          To reopen this post, please select a new deadline. This action will
          make the post active and visible again with the specified expiration
          date.
        </p>
      </ConfirmationDialog>
      <Row>
        {data.length > 0 ? (
          data.map((item, idx) => {
            return (
              <Col key={idx} xs={12} md={6}>
                <JobPostCard
                  status={item.status}
                  data={item}
                  renderDiscriptionPattern={pattern}
                  onView={handleView}
                  onEdit={handleEdit}
                  onMarkAsClosed={item => {
                    setModal({
                      type: 'markClosed',
                      id: item._id,
                      date: ''
                    });
                  }}
                  onActive={item => {
                    setModal({
                      type: 'markActive',
                      id: item._id,
                      date: ''
                    });
                  }}
                  onReopen={item => {
                    setModal({
                      type: 'reopen',
                      id: item._id,
                      date: ''
                    });
                  }}
                />
              </Col>
            );
          })
        ) : (
          <></>
        )}
      </Row>
    </>
  );
}

export default JobPostList;
